import React from 'react';
import { Mic, Image, FileText, File, Grid3X3, Sheet, Trash2 } from 'lucide-react';

const FilePreviewArea = ({ uploadedFiles, onRemoveFile }) => {
    if (uploadedFiles.length === 0) return null;

    const getFileIcon = (fileType, fileName) => {
        const extension = fileName?.split('.').pop()?.toLowerCase();
        
        if (fileType && fileType.includes('audio')) {
            return { icon: <Mic size={20} />, color: 'text-green-500' };
        }
        
        if (fileType && fileType.includes('image')) {
            return { icon: <Image size={20} />, color: 'text-blue-500' };
        }
        
        // PDF files
        if (fileType?.includes('pdf') || extension === 'pdf') {
            return { icon: <FileText size={20} />, color: 'text-red-500' };
        }
        
        // Excel files
        if (extension === 'xlsx' || extension === 'xls' || 
            fileType?.includes('spreadsheet') || 
            fileType?.includes('excel')) {
            return { icon: <Sheet size={20} />, color: 'text-green-600' };
        }
        
        // CSV files
        if (extension === 'csv' || fileType?.includes('csv')) {
            return { icon: <Grid3X3 size={20} />, color: 'text-orange-500' };
        }
        
        // Word documents
        if (extension === 'doc' || extension === 'docx' || 
            fileType?.includes('document') || 
            fileType?.includes('word')) {
            return { icon: <FileText size={20} />, color: 'text-blue-600' };
        }
        
        // Text files
        if (extension === 'txt' || fileType?.includes('text')) {
            return { icon: <FileText size={20} />, color: 'text-gray-500' };
        }
        
        // Default file icon
        return { icon: <File size={20} />, color: 'text-gray-500' };
    };

    const getFileTypeLabel = (fileType, fileName) => {
        const extension = fileName?.split('.').pop()?.toUpperCase();
        
        if (fileType && fileType.includes('audio')) return 'AUDIO';
        if (fileType && fileType.includes('image')) return 'IMAGE';
        if (fileType?.includes('pdf') || extension === 'PDF') return 'PDF';
        if (['XLSX', 'XLS'].includes(extension)) return 'EXCEL';
        if (extension === 'CSV') return 'CSV';
        if (['DOC', 'DOCX'].includes(extension)) return 'WORD';
        if (extension === 'TXT') return 'TEXT';
        
        return extension || 'FILE';
    };

    return (
        <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 flex-shrink-0">
            {uploadedFiles.map(file => {
                const { icon, color } = getFileIcon(file.type, file.name);
                const typeLabel = getFileTypeLabel(file.type, file.name);
                const isImage = file.preview && file.type?.includes('image');
                
                return (
                    <div key={file.id} className="relative group bg-gray-100 dark:bg-gray-700 rounded-lg p-2 min-w-[72px]">
                        {isImage ? (
                            // Image preview
                            <img 
                                src={file.preview} 
                                alt={file.name} 
                                className="h-14 w-14 object-cover rounded-md" 
                            />
                        ) : (
                            // Document icon preview
                            <div className="h-14 w-14 flex flex-col items-center justify-center bg-white dark:bg-gray-600 rounded-md border border-gray-200 dark:border-gray-500">
                                <div className={`mb-1 ${color}`}>
                                    {icon}
                                </div>
                                <span className="text-[8px] font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                                    {typeLabel}
                                </span>
                            </div>
                        )}
                        
                        {/* File name */}
                        <div className="mt-1 text-center">
                            <span className="text-[10px] text-gray-600 dark:text-gray-300 block truncate max-w-[68px]" title={file.name}>
                                {file.name}
                            </span>
                            {/* File size if available */}
                            {file.size && (
                                <span className="text-[8px] text-gray-400 dark:text-gray-500">
                                    {(file.size / 1024).toFixed(1)}KB
                                </span>
                            )}
                        </div>
                        
                        {/* Remove button */}
                        <button 
                            onClick={() => onRemoveFile(file.id)} 
                            className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 focus:opacity-100 shadow-sm" 
                            aria-label={`Remove ${file.name}`}
                        >
                            <Trash2 size={10} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default FilePreviewArea;